// Timesheet export helpers — PDF, CSV, Excel. Operates on the currently
// filtered rows passed in (each is a ws_time_entries row + joined fields).
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { localDateStr } from "../wsConstants";

const COLUMNS = [
  { key: "entry_date", label: "Date" },
  { key: "username", label: "Employee" },
  { key: "project_name", label: "Project" },
  { key: "task_title", label: "Task" },
  { key: "description", label: "Description" },
  { key: "hours", label: "Hours" },
  { key: "minutes", label: "Minutes" },
  { key: "billable_label", label: "Billable" },
];

// Normalise an entry into flat, display-ready fields.
const toRow = (e) => ({
  entry_date: e.entry_date || "",
  username: e.username || "",
  project_name: e.project_name || "Global",
  task_title: e.task_title || "",
  description: e.description || "",
  // Decimal hours for payroll maths + exact minutes so short entries aren't
  // ambiguous (11 min reads as 0.18 h).
  hours: Math.round((e.duration_minutes / 60) * 100) / 100,
  minutes: Number(e.duration_minutes) || 0,
  billable_label: e.billable ? "Billable" : "Internal",
});

// Local day (toISOString is UTC and would name a late-evening export with
// the wrong date).
const fileStamp = () => localDateStr();

export function downloadTimesheetCSV(entries) {
  const rows = entries.map(toRow);
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = COLUMNS.map((c) => c.label).join(",");
  const body = rows
    .map((r) => COLUMNS.map((c) => esc(r[c.key])).join(","))
    .join("\n");
  const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
  triggerDownload(blob, `timesheet-${fileStamp()}.csv`);
}

export function downloadTimesheetExcel(entries) {
  const rows = entries.map(toRow);
  const aoa = [
    COLUMNS.map((c) => c.label),
    ...rows.map((r) => COLUMNS.map((c) => r[c.key])),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Timesheet");
  XLSX.writeFile(wb, `timesheet-${fileStamp()}.xlsx`);
}

export function downloadTimesheetPDF(entries, meta = {}) {
  const rows = entries.map(toRow);
  const doc = new jsPDF({ orientation: "landscape" });
  doc.setFontSize(14);
  doc.text("Timesheet", 14, 16);
  doc.setFontSize(9);
  const totalH = rows.reduce((s, r) => s + r.hours, 0);
  const sub = [meta.range, `${entries.length} entries`, `${Math.round(totalH * 100) / 100}h total`]
    .filter(Boolean)
    .join("  ·  ");
  if (sub) doc.text(sub, 14, 22);
  autoTable(doc, {
    startY: 26,
    head: [COLUMNS.map((c) => c.label)],
    body: rows.map((r) => COLUMNS.map((c) => r[c.key])),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [245, 75, 1] },
  });
  doc.save(`timesheet-${fileStamp()}.pdf`);
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
